import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../interface/component/injected-props.interface';
import styled from 'styled-components';
import { ColorEnum } from '../../enum/color.enum';
import { largeMobileMedia } from '../../util/style/large-mobile-media';
import { ISingleContactItemData } from '../../model/linked-data';
import { ContactListModel } from '../../model/block';
import { isDraftEmpty } from '../../util/draft/is-draft-empty.util';
import { draftToString } from '../../util/draft/draft-to-string.util';
import { ContactAvatarIcon, LinkedInIcon, MailIcon, PhoneIcon, PinIcon } from '../icons';
import { determineTextColor } from '../../util/style/determine-text-color';
import { BlockColorType } from '../../enum/block-color.enum';
import {getImageCloudinaryURL} from '../../util/url/get-image-cloudinary-url.util';

export const COMPACT_CONTACT_CARD_HORIZONTAL_MARGIN = 20;
export const COMPACT_CONTACT_CARD_WIDTH = 235;
const IMAGE_SIZE = 168;
const ICON_SIZE = 30;
const SPACE_BETWEEN_TEXT = 5;
const IMAGE_TOP_BOTTOM_MARGIN = 76;
const SUPPORT_TEXT_FONT_SIZE = 18;

export const compactStyle = (style: any) => (props: { theme: { _ContactIsCompact: boolean } }) => props.theme._ContactIsCompact ? style : largeMobileMedia(style);

const ContactImageWrapper = styled.div`
  background-color: ${ColorEnum.White};
  color: ${ColorEnum.ContentBackground};
  border-radius: 50%;
  width: ${IMAGE_SIZE}px;
  height: ${IMAGE_SIZE}px;
  flex: 0 0 ${IMAGE_SIZE}px;
  overflow: hidden;
  margin: ${IMAGE_TOP_BOTTOM_MARGIN}px 0;
  justify-content: center;
  display: flex;
  align-items: center;
  ${compactStyle(`
    margin: 0 0 15px 0;
  `)}
`;

const ContactImage = styled.img`
  max-width: ${IMAGE_SIZE}px;
  max-height: ${IMAGE_SIZE}px;
  height: auto;
  width: auto;
`;

const ContactCardWrapper = styled.div`
  display: flex;
  width: 450px;
  height: 320px;
  margin: 0 10px;
  justify-content: stretch;
  ${largeMobileMedia(`
    max-width: 100%;
    width: 100%;
  `)}
  ${compactStyle(`
    flex-direction: column;
    height: auto;
    flex: 0 0 auto;
    align-items: center;
    margin: 10px ${COMPACT_CONTACT_CARD_HORIZONTAL_MARGIN}px;
    width: ${COMPACT_CONTACT_CARD_WIDTH}px;
  `)}
`;

const ContactDetails = styled.div`
  display: flex;
  flex: 1 1 0px;
  margin-left: 43px;
  position: relative;
  height: 100%;
  flex-direction: column;
  ${compactStyle(`
    margin-left: 0;
    align-items: center;
    width: 100%;
    height: auto;
    flex: 0 0 auto;
    justify-content: center;
  `)}
`;

const ContactWrapper = styled.div`
  position: absolute;
  bottom: calc(40% + ${56 + SPACE_BETWEEN_TEXT}px);
  font-size: ${SUPPORT_TEXT_FONT_SIZE}px;
  color: ${(props: { backgroundColor: string }) => ColorEnum.getColor(determineTextColor(props.backgroundColor))(props)};
  ${compactStyle(`
    margin: 0;
    padding: 0;
    margin-bottom: ${SPACE_BETWEEN_TEXT}px;
    position: relative;
  `)}
`;

const ContactDataWrapper = styled.div`
  position: absolute;
  top: 40%;
  ${compactStyle(`
    position: relative;
  `)}
`;

const ContactNameWrapper = styled.div`
  font-size: 24px;
  color: ${(props: { backgroundColor: string }) => ColorEnum.getColor(determineTextColor(props.backgroundColor))(props)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  max-height: 3em;
  box-sizing: content-box;
  -webkit-box-orient: vertical;
  ${compactStyle(`
    text-align: center;
    position: relative;
    padding: 0;
    margin-bottom: ${SPACE_BETWEEN_TEXT}px;
  `)};
`;

const BottomContentWrapper = styled.div`
  margin-top: ${SPACE_BETWEEN_TEXT}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  ${compactStyle(`
    justify-content: center;
    position: relative;
    padding: 0;
    margin-bottom: ${SPACE_BETWEEN_TEXT}px;
  `)};
`;

const ContactTitleWrapper = styled.div`
  margin-top: ${SPACE_BETWEEN_TEXT}px;
  font-size: ${SUPPORT_TEXT_FONT_SIZE}px;
  color: ${(props: { backgroundColor: string }) => ColorEnum.getColor(determineTextColor(props.backgroundColor))(props)};
  ${compactStyle(`
    justify-content: center;
  `)};
`;

const ContactIconsWrapper = styled.div`
  display: flex;
  margin-top: ${(props: { hasSecondRow: boolean }) => props.hasSecondRow ? '20' : '5'}px;
  ${compactStyle(`
    justify-content: center;
  `)};
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  margin: 0 5px;
  background-color: ${(props: { backgroundColor: BlockColorType }) => ColorEnum.getColor(props.backgroundColor)(props as any)};
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  cursor: pointer;
`;

const StyledPin = styled(PinIcon)`
  color: ${(props: { color: BlockColorType }) => ColorEnum.getColor(props.color)(props as any)};
  margin: 9px;
  width: 18px;
  height: 18px;
` as any;

const StyledLinkedIn = styled(LinkedInIcon)`
  color: ${(props: { color: BlockColorType }) => ColorEnum.getColor(props.color)(props as any)};
  margin: 9px;
  width: 18px;
  height: 18px;
` as any;

const StyledMail = styled(MailIcon)`
  color: ${(props: { color: BlockColorType }) => ColorEnum.getColor(props.color)(props as any)};
  margin: 9px;
  width: 18px;
  height: 18px;
` as any;

const StyledPhone = styled(PhoneIcon)`
  color: ${(props: { color: BlockColorType }) => ColorEnum.getColor(props.color)(props as any)};
  margin: 9px;
  width: 18px;
  height: 18px;
` as any;

export interface IContactCard {
  item: ISingleContactItemData;
  contact: ContactListModel;
  socialButtonTextColor: BlockColorType;
  backgroundColor: BlockColorType;
  socialButtonBackgroundColor: BlockColorType;
}

@inject('state', 'dispatch')
@observer
export class ContactCard extends React.Component<IContactCard & IInjectedProps> {

  onPhoneIconClick = () => {
    this.props.dispatch.callUrl(this.props.item.phone);
  }

  onMailIconClick = () => {
    this.props.dispatch.sendEmail(this.props.item.email);
  }

  onLinkedIn = () => {
    this.props.dispatch.openNewTab(this.props.item.linkedInUrl);
  }

  onLocationIconClick = () => {
    console.log('ILocation click is not yet implemented.');
  }

  render() {
    const hasSecondRow = !isDraftEmpty(this.props.contact.secondRow, this.props.state, this.props.item);
    return (
      <ContactCardWrapper>
        <ContactImageWrapper> {this.props.item.image ?
          <ContactImage src={getImageCloudinaryURL(this.props.item.image, IMAGE_SIZE, IMAGE_SIZE)}/> : <ContactAvatarIcon/>}
        </ContactImageWrapper>
        <ContactDetails>
          <ContactWrapper
            backgroundColor={this.props.state.colors.getColor(this.props.backgroundColor)}
          >
            {draftToString(this.props.contact.title, this.props.state, this.props.item)}
          </ContactWrapper>
          <ContactDataWrapper>
            <ContactNameWrapper
              backgroundColor={this.props.state.colors.getColor(this.props.backgroundColor)}
            >
              {draftToString(this.props.contact.firstRow, this.props.state, this.props.item)}
            </ContactNameWrapper>
            <BottomContentWrapper>
              {hasSecondRow ?
                <ContactTitleWrapper backgroundColor={this.props.state.colors.getColor(this.props.backgroundColor)}>
                  {draftToString(this.props.contact.secondRow, this.props.state, this.props.item)}
                </ContactTitleWrapper> : undefined}
              <ContactIconsWrapper hasSecondRow={hasSecondRow}>
                {this.props.item.linkedInUrl ? <IconWrapper
                  onClick={this.onLinkedIn}
                  backgroundColor={this.props.socialButtonBackgroundColor}
                >
                  <StyledLinkedIn
                    color={this.props.socialButtonTextColor}
                  />
                </IconWrapper> : undefined}
                {this.props.item.location ? <IconWrapper
                  onClick={this.onLocationIconClick}
                  backgroundColor={this.props.socialButtonBackgroundColor}
                >
                  <StyledPin
                    color={this.props.socialButtonTextColor}
                  />
                </IconWrapper> : undefined}
                {this.props.item.phone ? <IconWrapper
                  title={this.props.item.phone}
                  onClick={this.onPhoneIconClick}
                  backgroundColor={this.props.socialButtonBackgroundColor}
                >
                  <StyledPhone
                    color={this.props.socialButtonTextColor}
                  />
                </IconWrapper> : undefined}
                {this.props.item.email ? <IconWrapper
                  title={this.props.item.email}
                  onClick={this.onMailIconClick}
                  backgroundColor={this.props.socialButtonBackgroundColor}
                >
                  <StyledMail
                    color={this.props.socialButtonTextColor}
                  />
                </IconWrapper> : undefined}
              </ContactIconsWrapper>
            </BottomContentWrapper>
          </ContactDataWrapper>
        </ContactDetails>
      </ContactCardWrapper>
    );
  }
}
